import React, { useMemo, useState } from "react";
import { Box, Typography, useMediaQuery, Modal, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { Images } from "../../../utils/Images";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DocumentSection = ({ campaignData }) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [selectedDocument, setSelectedDocument] = useState(null);

  const documents = useMemo(() => {
    if (Array.isArray(campaignData?.documents) && campaignData.documents.length > 0) {
      return campaignData.documents;
    }
    return [];
  }, [campaignData?.documents]);

  const subtitleDate = useMemo(() => {
    if (Array.isArray(campaignData?.documents) && campaignData.documents.length > 0) {
      const sorted = [...campaignData.documents].sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateB - dateA;
      });
      const formatted = formatDate(sorted[0]?.updatedAt || sorted[0]?.createdAt);
      if (formatted) return formatted;
    }
    return "";
  }, [campaignData?.documents]);

  // Keep after all hooks to avoid hook-order mismatch on routes
  if (!documents || documents.length === 0) {
    return null;
  }

  const resolveDocumentSrc = (doc) => {
    if (!doc) return "";
    if (typeof doc === "string") return doc;
    return doc.url || doc.image || doc.src || "";
  };

  const handleDocumentClick = (doc) => {
    const src = resolveDocumentSrc(doc);
    setSelectedDocument(src);
  };

  const handleCloseModal = () => {
    setSelectedDocument(null);
  };

  return (
    <Box
      sx={{
        p: isMobile ? "14px" : "30px",
        width: "100%",
        border: "1px solid #F1F1F1",
        borderRadius: "12px",
      }}
    >
      {/* Title */}
      <Typography
        variant="h4"
        component="h2"
        sx={{
          display: "flex",
          gap: "10px",
          fontSize: isMobile ? "14px" : "24px",
          fontWeight: "700",
          mb: isMobile ? "10px" : "20px",
          color: "#1D1D1B",
          borderBottom: "1px solid #E6E8EC",
          paddingBottom: isMobile ? "10px" : "22px",
        }}
      >
        <img src={Images.streamlineSharpStar} alt="Images.streamlineSharpStar" /> Document
      </Typography>

      {/* Subtitle - Only show if date exists */}
      {subtitleDate && (
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: isMobile ? "10px" : "14px",
            fontWeight: 700,
            color: "#151513",
            mb: isMobile ? "14px":"30px",
            backgroundColor: "#F5F8ED",
            padding: isMobile ? "7px 10px" : "16px",
            borderRadius: isMobile ?"3px":"8px",
          }}
        >
          Medical report{" "}
          <span style={{ color: "#8EA946", fontWeight: "800" }}>{subtitleDate}</span>
        </Typography>
      )}

      {/* Grid for documents */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile 
            ? "repeat(2, 1fr)" 
            : "repeat(4, 1fr)",
          gap: isMobile ? "14px" : "20px",
          rowGap: isMobile ? "15px" : "20px",
          "@media (max-width: 448px)": {
            gridTemplateColumns: "repeat(2, 1fr)",
          },
        }}
      >
        {documents.map((doc, i) => {
          const imageSrc = resolveDocumentSrc(doc);
          const key = doc?.id || i;

          return (
          <Box key={key}>
            <Box
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: 2,
                bgcolor: "#fff",
                height: "100%",
                width: "100%",
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
              onClick={() => handleDocumentClick(doc)}
            >
              <Box
                component="img"
                src={imageSrc}
                alt={`Document ${i + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          </Box>
        );
        })}
      </Box>

      {/* Document Preview Modal */}
      <Modal
        open={!!selectedDocument}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          outline: "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Document Image */}
          {selectedDocument && (
            <Box
              component="img"
              src={selectedDocument}
              alt="Document Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default DocumentSection;
